import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { IntegrationSkeletonTile } from "@components/common/skeleton-loading/integration-skeleton-tile";
import { TilesContainer } from "@components/common/tiles-container";
import { IntegrationsInProgressDialog } from "@components/integrations-dialog/integrations-in-progress-dialog";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useTokensService } from "@context-providers/tokens-service-provider";
import { BaseProjectIdProps } from "@custom-types/base-types";
import {
  IntegrationUserConnectionOrigin,
  ProjectLevelIntegrationDialog,
} from "@custom-types/integrations-types";
import { useToast } from "@hooks/use-toast";
import { Box } from "@mui/material";
import { IntegrationTile } from "@pages/project-details/project-integrations/integration-tile";
import { ProjectIntegrationsConnectDialog } from "@pages/project-details/project-integrations/project-integrations-connect-dialog";
import { ProjectIntegrationsConnectedDialog } from "@pages/project-details/project-integrations/project-integrations-connected-dialog";
import { ProjectIntegrationsDisconnectDialog } from "@pages/project-details/project-integrations/project-integrations-disconnect-dialog";
import { IntegrationsService } from "@services/integrations-service/integrations-service";
import { REAUTHORIZATION_NOT_COMPLETED } from "@services/integrations-service/integrations-service-constants";
import { ProjectIntegrationsMap } from "@services/integrations-service/integrations-service-types";
import {
  isProjectConnectedToAutodesk,
  isProjectConnectedToProcore,
} from "@services/integrations-service/integrations-utils";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";

/** Renders project integration page */
export function ProjectIntegrations({
  projectId,
}: BaseProjectIdProps): JSX.Element {
  const { handleErrorWithPage, handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();
  const { createDialog } = useSphereDialog();
  const { trackEvent } = useTrackEvent();
  const tokensService = useTokensService();
  const projectApiClient = useProjectApiClient({ projectId });

  const [integrationId, setIntegrationId] =
    useState<FaroCoreApiTypes.IntegrationId>("autodesk");
  const [shouldShowSkeleton, setShouldShowSkeleton] = useState<boolean>(true);
  const [integrationTokens, setIntegrationTokens] = useState<
    FaroCoreApiTypes.IntegrationToken[]
  >([]);
  const [projectIntegrations, setProjectIntegrations] = useState<
    ProjectIntegrationsMap | undefined
  >(undefined);
  const [dialogState, setDialogState] =
    useState<ProjectLevelIntegrationDialog>("none");

  const userConnections: Record<FaroCoreApiTypes.IntegrationId, boolean> =
    useMemo(() => {
      const userIntegrations = integrationTokens.map((token) => token.provider);

      return {
        ["autodesk"]: userIntegrations.includes("autodesk"),
        ["procore"]: userIntegrations.includes("procore"),
      };
    }, [integrationTokens]);

  const projectConnections: Record<FaroCoreApiTypes.IntegrationId, boolean> =
    useMemo(() => {
      return {
        ["autodesk"]: isProjectConnectedToAutodesk(projectIntegrations),
        ["procore"]: isProjectConnectedToProcore(projectIntegrations),
      };
    }, [projectIntegrations]);

  /**
   * Handles the token re-authorization:
   * 1. Shows a dialog to inform the user that the integration needs to be authorized again
   * 2. After confirmation the authorization workflow initiates. Once the integration is
   * authorized again the user can resume the project connection workflow.
   * If the reauthorization then both reauthorization and project connection dialogs are closed
   * and an specific error is thrown.
   */
  const reauthorize = useCallback(
    async (integrationId: FaroCoreApiTypes.IntegrationId): Promise<void> => {
      try {
        await createDialog(
          {
            title: "Re-authorization required",
            isHideCancel: true,
            isHideClose: true,
          },
          `You need to authorize ${INTEGRATION_INFO[integrationId].displayName} again`
        );
        setDialogState("userConnection");

        const updatedTokens = await tokensService.authorizeIntegration(
          integrationId
        );
        setIntegrationTokens(updatedTokens);

        setDialogState("none");
        showToast({
          message: "Integration successfully re-authorized",
          type: "success",
        });
      } catch {
        setDialogState("none");
        throw new Error(REAUTHORIZATION_NOT_COMPLETED);
      }
    },
    [createDialog, showToast, tokensService]
  );

  const integrationsService = useMemo(() => {
    return new IntegrationsService({
      projectApiClient,
      reauthorize,
    });
  }, [projectApiClient, reauthorize]);

  /**
   * Closes the user connection browser tab, the "user connection in progress" dialog and the
   * "project connection" dialog.
   */
  const cancelUserConnection = useCallback(() => {
    tokensService.closeAuthorizationWindow();
    setDialogState("none");

    showToast({
      message: "Couldn't enable the integration",
      description: "The reauthorization was canceled.",
      type: "warning",
    });
  }, [showToast, tokensService]);

  // Connects the integration at user level and shows a success toast.
  // Returns whether the connection succeeded.
  const connectAtUserLevel = useCallback(
    async (integrationId: FaroCoreApiTypes.IntegrationId): Promise<boolean> => {
      trackEvent({
        name: UserProfile.connectIntegration,
        props: {
          integrationId,
          from: IntegrationUserConnectionOrigin.projectIntegrations,
        },
      });

      setDialogState("userConnection");

      try {
        const updatedTokens = await tokensService.authorizeIntegration(
          integrationId
        );
        setIntegrationTokens(updatedTokens);
        showToast({
          message: "Integration connected",
          type: "success",
        });
        return true;
      } catch (error) {
        handleErrorWithToast({
          id: `connectIntegration-${Date.now().toString()}`,
          title: "Failed to connect to the integration.",
          error,
        });
        return false;
      }
    },
    [handleErrorWithToast, showToast, tokensService, trackEvent]
  );

  // Callback triggered when the integration tile action button is clicked.
  // It carries out an specific action depending on the integration connection state.
  const onActionButtonClick = useCallback(
    async (integrationId: FaroCoreApiTypes.IntegrationId) => {
      setIntegrationId(integrationId);

      const isConnectedAtProjectLevel = projectConnections[integrationId];
      const isConnectedAtUserLevel = userConnections[integrationId];

      // If integration is connected at project level
      // then the button action is to disconnect the integration at project level
      if (isConnectedAtProjectLevel) {
        setDialogState("disconnect");
      }

      // If the integration is disconnected at both project and user level
      // then the button action is to connect the integration at both user and project level
      if (!isConnectedAtProjectLevel && !isConnectedAtUserLevel) {
        const isSuccess = await connectAtUserLevel(integrationId);
        setDialogState(isSuccess ? "connect" : "none");
      }

      // If the integration is disconnected at project level but connected at user level
      // then the button action is to connect the integration at project level
      if (!isConnectedAtProjectLevel && isConnectedAtUserLevel) {
        setDialogState("connect");
      }
    },
    [connectAtUserLevel, projectConnections, userConnections]
  );

  // Fetches and sets the required data on component load
  useEffect(() => {
    async function getData(): Promise<void> {
      try {
        const [projectIntegrations, integrationTokens] = await Promise.all([
          integrationsService.getProjectIntegrations(),
          tokensService.getIntegrationTokens(),
        ]);

        setProjectIntegrations(projectIntegrations);
        setIntegrationTokens(integrationTokens);
      } catch (error) {
        handleErrorWithPage({
          id: `getProjectIntegrationsData-${Date.now().toString()}`,
          title:
            "Failed to load the project integrations data. Please refresh the page and try again.",
          error,
        });
      }

      setShouldShowSkeleton(false);
    }

    void getData();
  }, [handleErrorWithPage, integrationsService, tokensService]);

  return (
    <Box component="div" sx={{ my: "25px" }}>
      <TilesContainer>
        {shouldShowSkeleton
          ? [...Array(2)].map((_, index) => (<IntegrationSkeletonTile key={index} />))
          : FaroCoreApiConst.allIntegrationIds.map((id) => (
              <IntegrationTile
                key={id}
                integrationId={id}
                isEnabled={projectConnections[id]}
                onActionButtonClick={onActionButtonClick}
                projectName={projectIntegrations?.[id]?.projectName}
              />
            ))}
      </TilesContainer>

      {dialogState === "connected" && (
        <ProjectIntegrationsConnectedDialog
          integrationId={integrationId}
          onClose={() => setDialogState("none")}
        />
      )}

      {dialogState === "connect" && (
        <ProjectIntegrationsConnectDialog
          integrationsService={integrationsService}
          integrationId={integrationId}
          setProjectIntegrations={setProjectIntegrations}
          setDialogState={setDialogState}
        />
      )}

      {dialogState === "disconnect" && (
        <ProjectIntegrationsDisconnectDialog
          integrationsService={integrationsService}
          integrationId={integrationId}
          setProjectIntegrations={setProjectIntegrations}
          onClose={() => setDialogState("none")}
        />
      )}

      {dialogState === "userConnection" && (
        <IntegrationsInProgressDialog
          integrationId={integrationId}
          dialogCancelCallback={cancelUserConnection}
        />
      )}
    </Box>
  );
}
